import { updateSessionById } from '@/pages/api/scribeAPI';
import { toast } from '@heidi/design-system/toast';

import { invalidateSessionsQuery } from '@/hooks/query/useQueryCombinedSessions';
import { logger } from '@/utils/logger/logger';
import { isOfflineLocalSession } from '@/utils/offline-recording/localSessionSettings';
import { type components } from '@heidi/schema/ml-scribe';
import {
  GET_SESSION_BY_ID,
  type QuerySessionResponse,
  type SessionResponse,
  type SessionsResponse,
  type SessionUpdate,
} from '@heidi/session/contracts';
import { sessionQueryVersionAtom } from '@heidi/session/data';
import { type InfiniteData, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { isEmpty } from 'lodash-es';
import { GET_SESSIONS } from '../query/useQuerySessions';
import { GET_INFINITE_SESSIONS } from '../query/useQuerySessionsV2';
import { GET_INFINITE_SESSIONS_WITH_PAGINATION } from '../query/useQuerySessionsWithPagination';

type Props = {
  sessionId: string;
  patientDetails: components['schemas']['PatientUpdate'];
};

export const UPDATE_PATIENT_DETAILS = 'UpdatePatientDetails';

export function useMutateUpdatePatientDetailsFromSession() {
  const queryClient = useQueryClient();
  const sessionQueryVersion = useAtomValue(sessionQueryVersionAtom);

  return useMutation({
    mutationKey: [UPDATE_PATIENT_DETAILS],
    retry: 3,
    mutationFn: async ({ patientDetails, sessionId }: Props) => {
      const session = queryClient.getQueryData<QuerySessionResponse>([
        GET_SESSION_BY_ID,
        { sessionId },
      ]);
      if (isOfflineLocalSession(session)) {
        return;
      }

      const payload: SessionUpdate = {
        patient: {
          ...patientDetails,
        },
      };

      return await updateSessionById(sessionId, payload);
    },

    onMutate({ patientDetails, sessionId }) {
      queryClient.setQueryData<SessionResponse>(
        [GET_SESSION_BY_ID, { sessionId: sessionId }],
        (oldData) => {
          if (!oldData) {
            return undefined;
          }
          const newData: SessionResponse = {
            ...oldData,
            patient: {
              ...oldData.patient,
              ...patientDetails,
            },
            session_name:
              patientDetails?.demographic_details ||
              patientDetails?.name ||
              oldData.session_name,
          };
          return newData;
        }
      );

      switch (sessionQueryVersion) {
        case 'v3':
        case 'v2':
          queryClient.setQueriesData<InfiniteData<SessionsResponse>>(
            {
              queryKey: [
                sessionQueryVersion === 'v3'
                  ? GET_INFINITE_SESSIONS_WITH_PAGINATION
                  : GET_INFINITE_SESSIONS,
              ],
            },
            (data: InfiniteData<SessionsResponse> | undefined) => {
              if (!data) return data;
              if (data.pages.length === 0) return data;
              return {
                ...data,
                pages: data.pages.map((page) => {
                  return {
                    ...page,
                    sessions: page.sessions.map((session) => {
                      if (session.session_id === sessionId) {
                        return {
                          ...session,
                          patient: {
                            ...session.patient,
                            ...patientDetails,
                          },
                          session_name:
                            patientDetails?.demographic_details ||
                            patientDetails?.name ||
                            session.session_name,
                        };
                      }
                      return session;
                    }),
                  };
                }),
              };
            }
          );
          break;
        default:
          queryClient.setQueryData<SessionsResponse>([GET_SESSIONS], (oldSessions) => {
            if (!oldSessions || isEmpty(patientDetails)) {
              return oldSessions;
            }

            // update the session in the list
            const newSessions = oldSessions.sessions.map((session) => {
              if (session.session_id === sessionId) {
                return {
                  ...session,
                  patient: {
                    ...session.patient,
                    ...patientDetails,
                  },
                  session_name:
                    patientDetails?.demographic_details ||
                    patientDetails?.name ||
                    session.session_name,
                };
              }

              return session;
            });

            return {
              sessions: newSessions,
              count: newSessions.length,
              has_next_page: oldSessions.has_next_page ?? false,
            };
          });
      }
    },

    onSuccess: (_, variables) => {
      const session = queryClient.getQueryData<QuerySessionResponse>([
        GET_SESSION_BY_ID,
        { sessionId: variables.sessionId },
      ]);
      if (isOfflineLocalSession(session)) return;
      void invalidateSessionsQuery();
    },
    onError: (error) => {
      logger.error('Error:', error);
      toast({
        tone: 'destructive',
        title: 'Backend Error',
        // description: error.errors[0]?.message,
      });
    },
  });
}
